import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  ButtonGroup,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import BoltIcon from '@mui/icons-material/Bolt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PageWithTitle } from 'src/Common/components';
import { BootDomainGate } from '../Predictions/components/system/BootDomainGate';
import { ControlPlaneButtons } from '../Predictions/components/system/ControlPlaneButtons';
import { PairTable } from './components/PairTable';
import { ManualPairDialog } from './components/ManualPairDialog';
import { AutoMapEventDialog } from './components/AutoMapEventDialog';
import { EventAgentRunDrawer } from './components/EventAgentRunDrawer';
import { RejectPairDialog } from './components/RejectPairDialog';
import { MatchingHistoryTab } from './components/MatchingHistoryTab';
import { RunSelector } from './components/RunSelector';
import {
  CategoryTabs,
  HISTORY_TAB,
  ALL_TAB,
  MapperTabValue,
} from './components/CategoryTabs';
import { SubcategoryChips } from './components/SubcategoryChips';
import { PredictionCategory } from '../Predictions/constants/categories';
import {
  usePairs,
  usePairStatsByCategory,
  useConfirmPair,
  useManualConfirmPair,
  useRunMatching,
  useFetchMarkets,
  useFetchProgress,
  useTriggerCryptoMatch,
  useTriggerDailyTempMatch,
  useSchedules,
  useToggleSchedule,
} from './api/usePairMapper';

import { SCHEDULE_RULE_LABELS, SCHEDULE_RULE_ORDER } from './scheduleRules';
const SCHEDULE_TOOLTIP = {
  hourly:
    'Hourly full pipeline. ON pins reserved concurrency to 6 (worker fan-out).',
  deterministic:
    'Deterministic-only, single invocation. Runs on a dedicated Lambda with static reserved concurrency 2 — independent of the hourly toggle.',
} as const;

const MATCH_TYPES = [
  { value: '', label: 'All' },
  { value: 'manual', label: 'Manual' },
  { value: 'deterministic', label: 'Deterministic' },
  { value: 'ai_confirmed', label: 'AI Confirmed' },
  { value: 'keyword_fallback', label: 'Keyword Fallback' },
  { value: 'ai_pending', label: 'AI Pending (legacy)' },
  { value: 'keyword_pending', label: 'KW Pending (legacy)' },
];

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'expired', label: 'Expired' },
];

const ACTIVE_TAB_KEY = 'predictionMapper.activeTab';

function readInitialTab(): MapperTabValue {
  try {
    const stored = window.localStorage.getItem(ACTIVE_TAB_KEY);
    if (stored) return stored as MapperTabValue;
  } catch {
    /* SSR / disabled storage — fall through */
  }
  return ALL_TAB;
}

export const PredictionMapperPage = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState('');
  const [matchedBy, setMatchedBy] = useState('');
  const [search, setSearch] = useState('');
  const [manualPairOpen, setManualPairOpen] = useState(false);
  const [autoMapOpen, setAutoMapOpen] = useState(false);
  const [agentRunId, setAgentRunId] = useState<string | null>(null);
  const [rejectPairId, setRejectPairId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<MapperTabValue>(readInitialTab);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(
    null,
  );
  const [runId, setRunId] = useState<string | null>(null);
  const [fetchSnack, setFetchSnack] = useState<{
    severity: 'info' | 'success' | 'error';
    message: string;
  } | null>(null);
  // Set at Fetch click; the polling hook watches recent runs newer than this
  // and reports per-platform fetched/new once each lambda lands.
  const [fetchBaselineMs, setFetchBaselineMs] = useState<number | null>(null);
  // Toolbar dropdown anchors. `null` = closed.
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [fetchAnchor, setFetchAnchor] = useState<HTMLElement | null>(null);
  const [runOptsAnchor, setRunOptsAnchor] = useState<HTMLElement | null>(null);
  const [schedulesAnchor, setSchedulesAnchor] = useState<HTMLElement | null>(
    null,
  );

  // Persist tab choice so the user lands back where they left off. Wrapped in
  // try/catch since localStorage can throw in private-browsing or SSR.
  useEffect(() => {
    try {
      window.localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    } catch {
      /* noop */
    }
  }, [activeTab]);

  // Clear subcategory when switching category — `nfl` makes no sense under
  // Crypto, and stale state would silently filter every row out.
  useEffect(() => {
    setActiveSubcategory(null);
    setPage(0);
  }, [activeTab, runId]);

  const isCategoryTab = activeTab !== HISTORY_TAB;
  const isAllTab = activeTab === ALL_TAB;
  // ALL_TAB browses the whole pair collection (no category filter), sorted by
  // createdAt desc — the service already sorts that way by default.
  const activeCategory =
    isCategoryTab && !isAllTab ? (activeTab as PredictionCategory) : null;

  const { data: stats } = usePairStatsByCategory(runId);

  const { data, isLoading } = usePairs({
    status: status || undefined,
    matchedBy: matchedBy || undefined,
    search: search || undefined,
    category: activeCategory ?? undefined,
    subcategory: activeSubcategory ?? undefined,
    runId: runId ?? undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const confirmMutation = useConfirmPair();
  const manualConfirmMutation = useManualConfirmPair();
  const matchMutation = useRunMatching();
  const fetchMutation = useFetchMarkets();
  const cryptoMatchMutation = useTriggerCryptoMatch();
  const dailyTempMatchMutation = useTriggerDailyTempMatch();
  const fetchProgress = useFetchProgress(fetchBaselineMs);
  const schedulesQuery = useSchedules();
  const toggleSchedule = useToggleSchedule();

  // Live message reflecting what the polling hook has seen so far.
  const fetchProgressMsg = useMemo(() => {
    const fmt = (n?: number | null) =>
      n == null ? '…' : n.toLocaleString('en-US');
    const poly = fetchProgress.polymarket;
    const kalshi = fetchProgress.kalshi;
    const polyStr = poly
      ? `Polymarket ✓ ${fmt(poly.marketsFetched)} fetched · ${fmt(poly.newMarketsFetched)} new`
      : 'Polymarket …';
    const kalshiStr = kalshi
      ? `Kalshi ✓ ${fmt(kalshi.marketsFetched)} fetched · ${fmt(kalshi.newMarketsFetched)} new`
      : 'Kalshi …';
    return `${polyStr}  ·  ${kalshiStr}`;
  }, [fetchProgress.polymarket, fetchProgress.kalshi]);

  // While polling: keep the Snackbar in sync with the live message.
  useEffect(() => {
    if (fetchBaselineMs === null) return;
    setFetchSnack({ severity: 'info', message: fetchProgressMsg });
  }, [fetchBaselineMs, fetchProgressMsg]);

  // Both platforms reported → success terminal state, stop polling.
  useEffect(() => {
    if (fetchBaselineMs === null) return;
    if (!fetchProgress.done) return;
    setFetchSnack({ severity: 'success', message: fetchProgressMsg });
    setFetchBaselineMs(null);
  }, [fetchBaselineMs, fetchProgress.done, fetchProgressMsg]);

  // Safety timeout: lambda fetch can take longer than 60s on a full run; stop
  // polling so the page doesn't sit on a stale "Fetching…" message forever.
  // The user can still open Run History to watch progress past this point.
  useEffect(() => {
    if (fetchBaselineMs === null) return;
    const id = setTimeout(() => {
      setFetchBaselineMs(null);
      setFetchSnack((prev) =>
        prev?.severity === 'success'
          ? prev
          : {
              severity: 'info',
              message:
                'Fetch still running — open Run History to watch the per-platform counts as each lambda completes.',
            },
      );
    }, 90_000);
    return () => clearTimeout(id);
  }, [fetchBaselineMs]);

  const isMatchRunning = matchMutation.isLoading;
  const scheduleState = schedulesQuery.data;
  const schedulesLoading =
    schedulesQuery.isLoading || toggleSchedule.isLoading;
  // Live "N/4 on" summary on the toolbar button.
  const schedulesSummary = useMemo(() => {
    const rules = scheduleState?.rules ?? [];
    const on = rules.filter((r) => r.running).length;
    const total = SCHEDULE_RULE_ORDER.length;
    return on === 0 ? 'All off' : `Schedules: ${on}/${total} on`;
  }, [scheduleState]);
  // Rules sorted into the fixed display order.
  const orderedRules = useMemo(() => {
    const byName = new Map(
      (scheduleState?.rules ?? []).map((r) => [r.name, r]),
    );
    return SCHEDULE_RULE_ORDER.map((name) => byName.get(name)).filter(
      (r): r is NonNullable<typeof r> => r != null,
    );
  }, [scheduleState]);

  const handleConfirm = useCallback(
    (id: string) => confirmMutation.mutate(id),
    [confirmMutation],
  );

  const handleManualConfirm = useCallback(
    (id: string) => manualConfirmMutation.mutate(id),
    [manualConfirmMutation],
  );

  // Reason-gated unmatch: the dialog handles a plain reject AND, when the
  // operator supplies a reason, dispatches a fix-match agent run.
  const handleReject = useCallback((id: string) => setRejectPairId(id), []);

  // ── Toolbar actions (consolidated under Add / Fetch / Run dropdowns) ──
  const handleFetchMarkets = useCallback(() => {
    // Capture baseline BEFORE invoking — the polling hook ignores any
    // pre-existing runs older than this. If the lambda invoke itself
    // errors, clear the baseline to stop polling.
    const clickedAt = Date.now();
    setFetchBaselineMs(clickedAt);
    setFetchSnack({
      severity: 'info',
      message:
        'Fetch started for Polymarket + Kalshi — waiting for each lambda…',
    });
    fetchMutation.mutate(undefined, {
      onError: (err: any) => {
        setFetchBaselineMs(null);
        setFetchSnack({
          severity: 'error',
          message:
            'Fetch failed: ' +
            (err?.response?.data?.message ||
              err?.message ||
              'unknown error'),
        });
      },
    });
  }, [fetchMutation]);

  const handleCryptoMatch = useCallback(() => {
    cryptoMatchMutation.mutate(
      {},
      {
        onSuccess: (res: any) =>
          setFetchSnack({
            severity: res?.invoked ? 'info' : 'error',
            message: res?.invoked
              ? 'Crypto 15m matcher started — new pairs land in Run History within ~3 min.'
              : `Crypto matcher invoke failed: ${
                  res?.message || 'unknown error'
                }`,
          }),
        onError: (err: any) =>
          setFetchSnack({
            severity: 'error',
            message:
              'Crypto matcher invoke failed: ' +
              (err?.response?.data?.message ||
                err?.message ||
                'unknown error'),
          }),
      },
    );
  }, [cryptoMatchMutation]);

  const handleDailyTempMatch = useCallback(() => {
    dailyTempMatchMutation.mutate(
      {},
      {
        onSuccess: (res: any) =>
          setFetchSnack({
            severity: res?.invoked ? 'info' : 'error',
            message: res?.invoked
              ? 'Daily temp matcher started — new pairs land in Run History within ~3 min.'
              : `Daily temp matcher invoke failed: ${
                  res?.message || 'unknown error'
                }`,
          }),
        onError: (err: any) =>
          setFetchSnack({
            severity: 'error',
            message:
              'Daily temp matcher invoke failed: ' +
              (err?.response?.data?.message ||
                err?.message ||
                'unknown error'),
          }),
      },
    );
  }, [dailyTempMatchMutation]);

  // Surface a backend `{ invoked:false, message }` (e.g. target Lambda's
  // reserved concurrency is 0) instead of silently treating the 201 as a
  // success. Plain runs that don't return the shape are left untouched.
  const runMatchingHandlers = useMemo(
    () => ({
      onSuccess: (res: any) => {
        if (res && res.invoked === false) {
          setFetchSnack({
            severity: 'error',
            message:
              res.message || 'Matching run was not invoked (unknown reason).',
          });
        }
      },
      onError: (err: any) =>
        setFetchSnack({
          severity: 'error',
          message:
            'Matching run failed: ' +
            (err?.response?.data?.message || err?.message || 'unknown error'),
        }),
    }),
    [],
  );

  const handleRunAll = useCallback(
    () => matchMutation.mutate({}, runMatchingHandlers),
    [matchMutation, runMatchingHandlers],
  );
  const handleRunTier = useCallback(
    (tiers: string) => matchMutation.mutate({ tiers }, runMatchingHandlers),
    [matchMutation, runMatchingHandlers],
  );
  const handleFullRun = useCallback(() => {
    if (
      confirm(
        'Full run: fetch all markets, match all unpaired with all tiers. Continue?',
      )
    ) {
      matchMutation.mutate({ fullRun: true }, runMatchingHandlers);
    }
  }, [matchMutation, runMatchingHandlers]);

  const rejectingPair = data?.pairs.find((p) => p._id === rejectPairId);

  return (
    <PageWithTitle>
      <Box px={2} py={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>
            Prediction Pair Mapper
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Both pods that serve this page, each with its own Boot/Shutdown.
                They live in the header, above the gates, so they stay reachable
                exactly when the pods they raise are down. */}
            <ControlPlaneButtons domain="matching" />
            <ControlPlaneButtons domain="opportunities" />
            {/* ── Schedules ▾ — four per-rule matcher toggles ── */}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                schedulesLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={(e) => setSchedulesAnchor(e.currentTarget)}
            >
              {schedulesSummary}
            </Button>
            <Popover
              anchorEl={schedulesAnchor}
              open={!!schedulesAnchor}
              onClose={() => setSchedulesAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box px={2} py={1.5} sx={{ minWidth: 260 }}>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.7, display: 'block', mb: 1 }}
                >
                  {`Concurrency — poly:${
                    scheduleState?.concurrency.polymarket ?? '—'
                  } kalshi:${
                    scheduleState?.concurrency.kalshi ?? '—'
                  } det:${scheduleState?.concurrency.deterministic ?? '—'}`}
                </Typography>
                <Stack spacing={0.5}>
                  {orderedRules.map((rule) => (
                    <Tooltip
                      key={rule.name}
                      title={SCHEDULE_TOOLTIP[rule.kind]}
                      placement="left"
                    >
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Switch
                            size="small"
                            checked={rule.running}
                            onChange={(e) =>
                              toggleSchedule.mutate({
                                name: rule.name,
                                enabled: e.target.checked,
                              })
                            }
                            disabled={schedulesLoading}
                            color="success"
                          />
                        }
                        label={SCHEDULE_RULE_LABELS[rule.name]}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            </Popover>
            {/* ── Add ▾ — Manual Pair / Auto-Map Event ── */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddLinkIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setAddAnchor(e.currentTarget)}
            >
              Add
            </Button>
            <Menu
              anchorEl={addAnchor}
              open={!!addAnchor}
              onClose={() => setAddAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setManualPairOpen(true);
                  setAddAnchor(null);
                }}
              >
                <ListItemIcon>
                  <AddLinkIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Manual Pair"
                  secondary="Create one pair from market IDs"
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAutoMapOpen(true);
                  setAddAnchor(null);
                }}
              >
                <ListItemIcon>
                  <AutoFixHighIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Auto-Map Event"
                  secondary="Headless agent scrapes + pairs one event"
                />
              </MenuItem>
            </Menu>

            {/* ── Fetch ▾ — Fetch Markets / Crypto 15m / Daily temp ── */}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                fetchMutation.isLoading ||
                cryptoMatchMutation.isLoading ||
                dailyTempMatchMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudDownloadIcon />
                )
              }
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setFetchAnchor(e.currentTarget)}
              disabled={
                fetchMutation.isLoading ||
                cryptoMatchMutation.isLoading ||
                dailyTempMatchMutation.isLoading ||
                isMatchRunning
              }
            >
              Fetch
            </Button>
            <Menu
              anchorEl={fetchAnchor}
              open={!!fetchAnchor}
              onClose={() => setFetchAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  handleFetchMarkets();
                  setFetchAnchor(null);
                }}
              >
                <ListItemIcon>
                  <CloudDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Fetch Markets"
                  secondary="Pull from Polymarket + Kalshi"
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCryptoMatch();
                  setFetchAnchor(null);
                }}
              >
                <ListItemIcon>
                  <CurrencyBitcoinIcon
                    fontSize="small"
                    sx={{ color: 'warning.main' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Crypto 15m"
                  secondary="Scoped fetch + crypto:coin match"
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDailyTempMatch();
                  setFetchAnchor(null);
                }}
              >
                <ListItemIcon>
                  <WbSunnyIcon fontSize="small" sx={{ color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Daily temp"
                  secondary="Scoped fetch + climate exact-bin + inverted-edge match"
                />
              </MenuItem>
            </Menu>

            {/* ── Run All (primary) + ▾ options split-button ── */}
            <ButtonGroup
              variant="contained"
              size="small"
              disabled={isMatchRunning}
            >
              <Button
                startIcon={
                  isMatchRunning ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={handleRunAll}
              >
                Run All
              </Button>
              <Button
                size="small"
                aria-label="more run options"
                onClick={(e) => setRunOptsAnchor(e.currentTarget)}
                sx={{ px: 0.5 }}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={runOptsAnchor}
              open={!!runOptsAnchor}
              onClose={() => setRunOptsAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  handleRunTier('deterministic');
                  setRunOptsAnchor(null);
                }}
              >
                <ListItemIcon>
                  <BoltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Deterministic only"
                  secondary="Tier 1 — free, no AI"
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleRunTier('deterministic,ai_confirmed');
                  setRunOptsAnchor(null);
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="+ AI"
                  secondary="Tier 1 + Tier 2"
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleRunTier('keyword_fallback');
                  setRunOptsAnchor(null);
                }}
              >
                <ListItemIcon>
                  <ManageSearchIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Keywords only"
                  secondary="Tier 3 — semantic AI"
                />
              </MenuItem>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
              <MenuItem
                onClick={() => {
                  handleFullRun();
                  setRunOptsAnchor(null);
                }}
                sx={{ color: 'warning.main' }}
              >
                <ListItemIcon>
                  <PlayArrowIcon
                    fontSize="small"
                    sx={{ color: 'warning.main' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Full Run"
                  secondary="Fetch ALL + all tiers — slow"
                />
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        {/* Two INDEPENDENT banners — the two pods scale to 0 separately, so
            either can be down alone. `banner`, never `replace`, for two reasons:
            `replace` would hide the Boot buttons in the header above, and a
            control-plane reading must never take a working screen away. Empty
            children: the surfaces these gate are the whole page below, which the
            banners sit on top of rather than wrap. */}
        <BootDomainGate domain="matching" mode="banner">
          <></>
        </BootDomainGate>
        <BootDomainGate domain="opportunities" mode="banner">
          <></>
        </BootDomainGate>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          mb={1}
          sx={{ color: 'white' }}
        >
          <RunSelector value={runId} onChange={setRunId} />
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            {runId
              ? 'Showing pairs from the selected run only.'
              : 'Showing all pairs across every run.'}
          </Typography>
        </Stack>

        <CategoryTabs
          value={activeTab}
          onChange={setActiveTab}
          stats={stats}
        />

        {isCategoryTab ? (
          <Box mt={2}>
            {activeCategory && (
              <SubcategoryChips
                category={activeCategory}
                value={activeSubcategory}
                onChange={setActiveSubcategory}
                stats={stats}
              />
            )}

            <Stack direction="row" spacing={2} mb={2}>
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 250 }}
              />
              <TextField
                size="small"
                label="Status"
                select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 140 }}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Match Type"
                select
                value={matchedBy}
                onChange={(e) => {
                  setMatchedBy(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 180 }}
              >
                {MATCH_TYPES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <PairTable
              pairs={data?.pairs || []}
              total={data?.total || 0}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              isLoading={isLoading}
              onConfirm={handleConfirm}
              onManualConfirm={handleManualConfirm}
              onReject={handleReject}
            />
          </Box>
        ) : (
          <Box mt={2}>
            <MatchingHistoryTab
              onViewRunPairs={(nextRunId, category) => {
                // Jump from a run's summary into the scoped browse view:
                // pin the Run dropdown to that run and land on its largest
                // category tab. The effect above clears subcategory + page.
                setRunId(nextRunId);
                setActiveTab(category);
              }}
            />
          </Box>
        )}
      </Box>
      <ManualPairDialog
        open={manualPairOpen}
        onClose={() => setManualPairOpen(false)}
      />
      <AutoMapEventDialog
        open={autoMapOpen}
        onClose={() => setAutoMapOpen(false)}
        onRunStarted={(runId) => setAgentRunId(runId)}
      />
      <EventAgentRunDrawer
        runId={agentRunId}
        onClose={() => setAgentRunId(null)}
      />
      <RejectPairDialog
        pairId={rejectPairId}
        pairName={rejectingPair?.name}
        onClose={() => setRejectPairId(null)}
        onFixMatchStarted={(runId) => setAgentRunId(runId)}
      />
      <Snackbar
        open={!!fetchSnack}
        // While polling, keep it pinned so the live counts can update.
        // After polling ends, auto-hide success/info; errors require a click.
        autoHideDuration={
          fetchBaselineMs !== null
            ? null
            : fetchSnack?.severity === 'error'
              ? null
              : 8000
        }
        onClose={(_, reason) => {
          // Don't auto-dismiss on click-away while we're still polling.
          if (reason === 'clickaway' && fetchBaselineMs !== null) return;
          setFetchSnack(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {fetchSnack ? (
          <Alert
            severity={fetchSnack.severity}
            onClose={() => setFetchSnack(null)}
            variant="filled"
            sx={{ maxWidth: 480 }}
          >
            {fetchSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </PageWithTitle>
  );
};
